import { useState } from "react";
import { useSnackbar } from "notistack";
import { State } from "../../../../domain/model/ResponseState";
import { Customer } from "../../../../domain/model/Customer";
import { GetCustomerByIdUseCase } from "../../../../domain/usecase/customer/getById/UseCase";
import { Order, UpdateOrderModel } from "../../../../domain/model/Order";
import { GetCustomerOrdersUseCase } from "../../../../domain/usecase/customer/getOrders/UseCase";
import { UpdateOrderUseCase } from "../../../../domain/usecase/order/update/UseCase";
import {
  downloadBlob,
  showGlobalLoadingView,
} from "../../../../core/utils/Utils";
import { DownloadMultipleOrderReceiptsCase } from "../../../../domain/usecase/order/downloadMultipleReceipts/UseCase";

export function CustomerDetailsViewModel(
  getCustomerByIdUseCase: GetCustomerByIdUseCase,
  getCustomerOrdersUseCase: GetCustomerOrdersUseCase,
  updateOrderUseCase: UpdateOrderUseCase,
  downloadMultipleOrderReceiptsCase: DownloadMultipleOrderReceiptsCase
) {
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [customer, setCustomer] = useState<Customer>();
  const [orders, setOrders] = useState<Array<Order>>([]);

  async function fetchCustomer(id: string) {
    setLoading(true);
    const response = await getCustomerByIdUseCase.invoke(id);
    setLoading(false);
    switch (response.responseState) {
      case State.Success:
        setCustomer(response.data);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function fetchOrders(id: string) {
    setOrdersLoading(true);
    const response = await getCustomerOrdersUseCase.invoke(id);
    setOrdersLoading(false);
    switch (response.responseState) {
      case State.Success:
        if (!response.data) return;
        setOrders(response.data);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  async function updateOrder(id: string, model: UpdateOrderModel) {
    showGlobalLoadingView(true);
    const response = await updateOrderUseCase.invoke(id, model);
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        showSnackbar(response.data?.message, "success");
        setOrders((prev) =>
          prev
            ? prev.map((order) =>
                order._id === id && model.status !== undefined
                  ? { ...order, status: model.status }
                  : order
              )
            : prev
        );
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function selectUnselectOrder(orderId: string) {
    setOrders((prev) =>
      prev
        ? prev.map((order) =>
            order._id === orderId
              ? { ...order, selected: !order.selected }
              : order
          )
        : prev
    );
  }

  async function downloadMultipleOrderReceipts() {
    const selectedOrderIds = orders
      ?.filter((o) => o.selected)
      .flatMap((o) => o.orderId.toString());
    if (!selectedOrderIds || selectedOrderIds?.length == 0) {
      showSnackbar("No order was chosen");
      return;
    }
    showGlobalLoadingView(true);
    const response = await downloadMultipleOrderReceiptsCase.invoke(
      selectedOrderIds
    );
    showGlobalLoadingView(false);
    switch (response.responseState) {
      case State.Success:
        if (!response.data) return;
        downloadBlob(response.data, `receipts_${Date.now()}.zip`);
        break;
      case State.Fail:
      case State.Error:
        showSnackbar(response.error?.message);
        break;
    }
  }

  function showSnackbar(
    message: string | undefined,
    variant: "error" | "success" = "error"
  ) {
    if (message) enqueueSnackbar(message, { variant: variant });
  }

  return {
    isLoading,
    customer,
    fetchCustomer,
    orders,
    ordersLoading,
    fetchOrders,
    updateOrder,
    selectUnselectOrder,
    downloadMultipleOrderReceipts,
  };
}
